/**
 * MISSION DEBRIEFING — the screen a sortie is scored on.
 *
 * Split out of `career-screens.ts` when that file crossed the size gate. The
 * seam is a real one rather than a convenience: this is one of the two screens
 * reached from **both** doors into the game — `/career` after a career sortie
 * and `/fly` after a single mission, the latter through
 * `shell/debrief-surface.ts` — while everything left in `career-screens.ts` is
 * career furniture that `/fly` never sees. The other is the outcome sequence
 * that comes *before* it, in `simend-screens.ts`; this file held a single
 * hardcoded `SAFE RETURN...` in its place until G52.
 *
 * `mission-debriefing.png` is the capture it is measured against;
 * `docs/game/end-of-mission.md` is the evidence trail.
 */

import type { MissionDebrief } from "@red-opus/engine";
import type { Rect } from "../render/draw-ops.ts";
import type { FontAtlas } from "../render/text-metrics.ts";
import type { MenuAction } from "./menu-model.ts";
import { actionRow, wrapText } from "./panel-parts.ts";
import type { ShellScreen, Widget } from "./shell-screen.ts";

/**
 * `mission-debriefing.png`: an orange stats plate inside a tan frame, eight
 * black value wells and the sortie's narrative above it in TALK.FNT, which is
 * the one screen in the shell that uses that font. Every rect below is
 * measured.
 *
 * The tan frame's corners are square and the orange face's are **cut**, with a
 * rivet inside each — read off the capture pixel by pixel and drawn by
 * `plate-chrome.ts`'s `chamferedPlateOps` (G55). Before that this screen drew a
 * square orange face and no rivets, which was its last known departure from the
 * capture bar one: the rule under `VICTORIES`, added below at the same time.
 */
export const DEBRIEF_PLATE: Rect = { x: 29, y: 106, w: 266, h: 72 };
export const DEBRIEF_FACE: Rect = { x: 33, y: 109, w: 258, h: 66 };

/** The engraved rule under `VICTORIES`, spanning both count columns. Measured: 243 over 252 at y=122. */
export const DEBRIEF_VICTORIES_RULE = { x: 174, y: 122, w: 103 } as const;

/** A black well's frame from the interior the capture measures: two rings around it. */
function wellFrame(x: number, y: number, w: number): Rect {
  return { x: x - 2, y: y - 2, w: w + 4, h: 11 };
}

interface DebriefBox {
  readonly rect: Rect;
  readonly value: string;
}

function debriefBoxes(debrief: MissionDebrief): readonly DebriefBox[] {
  const count = (x: number, y: number, value: number): DebriefBox => ({ rect: wellFrame(x, y, 17), value: String(value) });
  return [
    { rect: wellFrame(47, 129, 66), value: String(debrief.missionScore) },
    { rect: wellFrame(73, 159, 17), value: String(debrief.aircraftLost) },
    // Two columns, three rows: PERSONAL is the player's own claims, SQUADRON is
    // his side's including his, so the right column is never below the left.
    count(191, 137, debrief.aircraftKills),
    count(247, 137, debrief.squadronVictories),
    count(191, 149, debrief.balloonKills),
    count(247, 149, debrief.squadronBalloonVictories),
    count(191, 161, debrief.zeppelinKills),
    count(247, 161, debrief.squadronZeppelinVictories),
  ];
}

/**
 * The narrative sits at x=35 from y=56 at pitch 12 — `mission-debriefing.png`
 * shows three rows there, in TALK.FNT.
 *
 * `lines: 4`, not 3, and the number is measured rather than chosen. Wrapping
 * every one of the 52 messages at `column` against the baked `TALK.FNT`, once
 * per target noun and ace name (1,456 combinations), the longest needs **four**
 * rows — *"Our %t was not bombed. All enemy bombers were shot down. None of our
 * fighters were destroyed. The mission was an outstanding success!"*. Four rows
 * end at y=100 and the plate's top edge is y=106, so they fit; five would run
 * into it. The capture happens to show a message that needs three.
 */
const DEBRIEF_NARRATIVE = { x: 35, y: 56, pitch: 12, column: 250, lines: 4 } as const;

/**
 * The debriefing's paragraph, as rows.
 *
 * **The original re-flows; it does not draw `REVIEW.DAT`'s stored lines.** The
 * file keeps message 1.0 as three NUL-separated strings ending *"…were shot
 * down,"* / *"nor did your flight…aircraft."* / *"The mission was uneventful."*,
 * and `mission-debriefing.png` draws it broken after *"shot"* and after *"any"*
 * — so the stored breaks are string-pool boundaries, not screen rows. Joining
 * on a space and wrapping to the panel is what reproduces the capture.
 *
 * `DEBRIEF_NARRATIVE.column` is measured: against the baked `TALK.FNT` atlas,
 * **every column from 237 to 258 reproduces the capture's three rows exactly**,
 * and 250 sits in the middle of that band. `debrief-screen.test.ts` asserts the
 * three rows, so the constant cannot drift out of it unnoticed.
 */
function debriefNarrativeLines(narrative: readonly string[], talk: FontAtlas): string[] {
  const paragraph = narrative.join(" ");
  return wrapText(talk, paragraph, DEBRIEF_NARRATIVE.column).slice(0, DEBRIEF_NARRATIVE.lines);
}

export function debriefingScreen(
  debrief: MissionDebrief,
  font: FontAtlas,
  talk: FontAtlas,
  action: MenuAction,
  /**
   * Which service the pilot flies for. The header's emblem is the squadron's
   * own — `pilotRecordScreen` already picks it this way, and a German pilot
   * being handed an RFC roundel at his own debriefing was a plain bug.
   */
  nation: "german" | "allied" = "allied",
): ShellScreen {
  const narrative = debriefNarrativeLines(debrief.narrative, talk);
  return {
    id: "career/debrief",
    chrome: {
      kind: "panel",
      title: "MISSION DEBRIEFING",
      emblem: nation === "german" ? "cross" : "roundel",
      background: undefined,
    },
    widgets: [
      {
        kind: "prose",
        block: { lines: narrative, x: DEBRIEF_NARRATIVE.x, y: DEBRIEF_NARRATIVE.y, role: "talk", pitch: DEBRIEF_NARRATIVE.pitch },
      },
      { kind: "plate", rect: DEBRIEF_PLATE },
      { kind: "orangePlate", rect: DEBRIEF_FACE },
      { kind: "text", text: "MISSION SCORE", x: 45, y: 114, role: "orangeHeading" },
      { kind: "text", text: "AIRCRAFT LOST", x: 45, y: 144, role: "orangeHeading" },
      { kind: "text", text: "VICTORIES", x: 201, y: 110, role: "orangeHeading" },
      { kind: "underline", ...DEBRIEF_VICTORIES_RULE, orange: true },
      { kind: "text", text: "PERSONAL", x: 179, y: 126, role: "orangeLabel" },
      { kind: "text", text: "SQUADRON", x: 234, y: 126, role: "orangeLabel" },
      { kind: "text", text: "AIRCRAFT", x: 135, y: 137, role: "orangeLabel" },
      { kind: "text", text: "BALLOONS", x: 135, y: 149, role: "orangeLabel" },
      { kind: "text", text: "ZEPPELINS", x: 135, y: 161, role: "orangeLabel" },
      ...debriefBoxes(debrief).flatMap((box): Widget[] => [
        // Sunk into the orange plate, so framed on the orange ramp — the port
        // drew tan rings here until the rendered screen was sampled against the
        // capture (see `ORANGE_SUNKEN_RINGS`).
        { kind: "well", rect: box.rect, orange: true },
        // Left, not centred. `well-alignment.test.ts` measures the offset off
        // `mission-debriefing.png` itself, on both well widths, rather than
        // restating it here where it could drift from the capture.
        { kind: "wellText", rect: box.rect, text: box.value, align: "left" },
      ]),
      ...actionRow([{ id: "continue", label: "CONTINUE", action }], font, 180),
    ],
    cancel: action,
  };
}
